import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Store, UserCog, PackageCheck, Clock, AlertTriangle } from "lucide-react";

const RANGES = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Last 90 Days", "This Month", "Custom"];
const STATUSES = ["Pending", "In Progress", "Completed", "Delivered"];

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Reports() {
  const { user } = useAuth();
  const [tab, setTab] = useState("branch"); // branch | operator
  const today = new Date().toISOString().slice(0, 10);
  const past = new Date(); past.setDate(past.getDate() - 29);
  const [dateFrom, setDateFrom] = useState(past.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today);
  const [rng, setRng] = useState("Last 30 Days");
  const [onlyMine, setOnlyMine] = useState(false);
  const [branch, setBranch] = useState(null);
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadBranch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/branch-wise", { params: { date_from: dateFrom, date_to: dateTo, rng } });
      setBranch(data);
    } finally { setLoading(false); }
  };
  const loadOp = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/operator-wise", { params: { date_from: dateFrom, date_to: dateTo, rng, only_mine: onlyMine } });
      setOp(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (tab === "branch") loadBranch(); else loadOp(); /* eslint-disable-next-line */ }, [tab, rng, onlyMine]);

  const totals = branch?.rows.reduce((acc, r) => {
    acc.count += r.total_count;
    acc.amount += r.total_amount;
    acc.paid += r.total_paid;
    acc.balance += r.total_balance;
    acc.completedNotDelivered += r.completed_not_delivered;
    return acc;
  }, { count: 0, amount: 0, paid: 0, balance: 0, completedNotDelivered: 0 }) || null;

  const opTotals = op?.rows.reduce((acc, r) => {
    acc.pending += r.pending_count;
    acc.inProgress += r.in_progress_count;
    acc.completed += r.completed_count;
    acc.delivered += r.delivered_count;
    return acc;
  }, { pending: 0, inProgress: 0, completed: 0, delivered: 0 }) || null;

  return (
    <div className="space-y-4" data-testid="reports-page">
      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3">
        <div className="inline-flex rounded-md overflow-hidden border border-slate-200">
          <button
            onClick={() => setTab("branch")}
            data-testid="report-tab-branch"
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors ${tab === "branch" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
          >
            <Store className="h-3.5 w-3.5" /> Branch-wise
          </button>
          <button
            onClick={() => setTab("operator")}
            data-testid="report-tab-operator"
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors ${tab === "operator" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
          >
            <UserCog className="h-3.5 w-3.5" /> Operator-wise
          </button>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Range</Label>
          <Select value={rng} onValueChange={setRng}>
            <SelectTrigger className="h-9 w-36" data-testid="report-range"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {RANGES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="report-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="report-to" />
        </div>
        {tab === "operator" && (
          <div className="flex items-center gap-2 h-9">
            <input
              type="checkbox"
              id="only-mine"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
              className="h-4 w-4 accent-[color:var(--brand-teal)]"
              data-testid="report-only-mine"
            />
            <label htmlFor="only-mine" className="text-xs font-semibold text-slate-700 cursor-pointer">
              Only my pending work
            </label>
          </div>
        )}
        <Button onClick={tab === "branch" ? loadBranch : loadOp} disabled={loading} className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="report-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Branch-wise report */}
      {tab === "branch" && branch && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryTile label="Records" value={totals?.count ?? 0} icon={PackageCheck} color="#2E77E6" />
            <SummaryTile label="Total Amount" value={inr(totals?.amount)} icon={PackageCheck} color="#186A65" />
            <SummaryTile label="Total Paid" value={inr(totals?.paid)} icon={PackageCheck} color="#2AA36F" />
            <SummaryTile label="Balance" value={inr(totals?.balance)} icon={AlertTriangle} color="#DC3D5E" />
            <SummaryTile label="Ready for Delivery" value={totals?.completedNotDelivered ?? 0} icon={Clock} color="#C87A2C" hint="Completed but not delivered" />
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="branch-report-table">
            <div className="px-4 py-2.5 border-b-2 border-[color:var(--brand-teal)] bg-slate-50 font-display font-bold text-slate-900 tracking-tight">
              Branch-wise Performance
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm crm-table">
                <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-semibold">Branch</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-amber-700">Pending</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-blue-700">In Progress</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-emerald-700">Completed (Not Delivered)</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-purple-700">Delivered</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {branch.rows.map((r) => (
                    <tr key={r.branch_id}>
                      <td className="px-3 py-2 font-semibold text-slate-800">{r.branch_name}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{r.total_count}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-amber-700">{r.by_status.Pending.count}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-blue-700">{r.by_status["In Progress"].count}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-emerald-700 font-bold">{r.completed_not_delivered}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-purple-700">{r.by_status.Delivered.count}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{inr(r.total_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{inr(r.total_paid)}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-red-600">{inr(r.total_balance)}</td>
                    </tr>
                  ))}
                  {branch.rows.length === 0 && (
                    <tr><td colSpan={9} className="py-8 text-center text-slate-400">No data in range</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Operator-wise report */}
      {tab === "operator" && op && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryTile label="Pending" value={opTotals?.pending ?? 0} icon={AlertTriangle} color="#E8A93A" />
            <SummaryTile label="In Progress" value={opTotals?.inProgress ?? 0} icon={Clock} color="#2E77E6" />
            <SummaryTile label="Completed" value={opTotals?.completed ?? 0} icon={PackageCheck} color="#2AA36F" hint="Ready for delivery" />
            <SummaryTile label="Delivered" value={opTotals?.delivered ?? 0} icon={PackageCheck} color="#8B5CF6" />
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="operator-report-table">
            <div className="px-4 py-2.5 border-b-2 border-[color:var(--brand-teal)] bg-slate-50 flex items-center justify-between">
              <div className="font-display font-bold text-slate-900 tracking-tight">Operator-wise Workload</div>
              {onlyMine && (
                <div className="text-xs text-slate-500">
                  Showing only entries assigned to <span className="font-semibold text-slate-800">{user?.name}</span>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm crm-table">
                <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-semibold">Operator</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-amber-700">Pending</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-blue-700">In Progress</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-emerald-700">Completed</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-purple-700">Delivered</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Open Load</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {op.rows.map((r) => {
                    const openLoad = r.pending_count + r.in_progress_count;
                    return (
                      <tr key={r.operator}>
                        <td className="px-3 py-2 font-semibold text-slate-800">{r.operator}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{r.total_count}</td>
                        <td className="px-3 py-2 text-right font-mono-num text-amber-700">{r.pending_count}</td>
                        <td className="px-3 py-2 text-right font-mono-num text-blue-700">{r.in_progress_count}</td>
                        <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{r.completed_count}</td>
                        <td className="px-3 py-2 text-right font-mono-num text-purple-700">{r.delivered_count}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{inr(r.total_amount)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${
                            openLoad === 0 ? "bg-emerald-50 text-emerald-700" :
                            openLoad < 5 ? "bg-amber-50 text-amber-700" :
                            "bg-red-50 text-red-700"
                          }`}>{openLoad}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {op.rows.length === 0 && (
                    <tr><td colSpan={8} className="py-8 text-center text-slate-400">No entries in range</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon, color, hint }) {
  return (
    <div className="rounded-md overflow-hidden bg-white border border-slate-200 flex">
      <div className="w-14 shrink-0 flex items-center justify-center" style={{ background: color }}>
        <Icon className="h-6 w-6 text-white" strokeWidth={1.8} />
      </div>
      <div className="flex-1 px-4 py-2.5">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-display font-black text-2xl tracking-tight text-slate-900 font-mono-num">{value}</div>
        {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
